// src/features/landing/SectionRenderer.tsx
// Read-only rendering of a single LandingSection — used by the editor's live
// preview pane and by the public page (/l/:slug). No editing here.

import type { LandingSection } from '@core/types/landing';

const FEATURE_COLS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-4',
};

function styleFor(section: LandingSection): React.CSSProperties {
  const s = section.style;
  if (!s) return {};
  return {
    backgroundColor: s.background_color || undefined,
    backgroundImage: s.background_image_url ? `url(${s.background_image_url})` : undefined,
    backgroundSize: s.background_image_url ? 'cover' : undefined,
    color: s.text_color || undefined,
    padding: s.padding || undefined,
  };
}

export function SectionRenderer({ section }: { section: LandingSection }) {
  if (section.hidden) return null;
  const wrapClass = section.style?.full_width ? 'w-full' : 'mx-auto w-full max-w-4xl';

  switch (section.section_type) {
    case 'LANDING_SECTION_TYPE_HERO': {
      const h = section.hero;
      if (!h) return null;
      return (
        <section style={styleFor(section)} className={`${wrapClass} px-6 py-16 text-center`}>
          {h.background_image_url && <div className="mb-4 h-48 w-full rounded-md bg-cover bg-center" style={{ backgroundImage: `url(${h.background_image_url})` }} />}
          <h2 className="text-3xl font-bold">{h.heading}</h2>
          {h.subheading && <p className="mt-2 text-lg opacity-80">{h.subheading}</p>}
          <div className="mt-6 flex justify-center gap-3">
            {h.primary_button?.label && (
              <a href={h.primary_button.url} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
                {h.primary_button.label}
              </a>
            )}
            {h.secondary_button?.label && (
              <a href={h.secondary_button.url} className="rounded-md border border-border px-4 py-2 text-sm font-medium">
                {h.secondary_button.label}
              </a>
            )}
          </div>
        </section>
      );
    }
    case 'LANDING_SECTION_TYPE_TEXT':
      return (
        <section style={styleFor(section)} className={`${wrapClass} whitespace-pre-wrap px-6 py-8 text-sm`}>
          {section.text?.body}
        </section>
      );
    case 'LANDING_SECTION_TYPE_IMAGE':
      return (
        <section style={styleFor(section)} className={`${wrapClass} px-6 py-8 text-center`}>
          {section.image?.image_url ? (
            // eslint-disable-next-line jsx-a11y/img-redundant-alt
            <img src={section.image.image_url} alt={section.image.alt_text || ''} className="mx-auto max-h-96 rounded-md" />
          ) : (
            <div className="mx-auto h-48 w-full max-w-lg rounded-md bg-muted" />
          )}
          {section.image?.caption && <p className="mt-2 text-xs text-muted-foreground">{section.image.caption}</p>}
        </section>
      );
    case 'LANDING_SECTION_TYPE_GALLERY':
      return (
        <section style={styleFor(section)} className={`${wrapClass} px-6 py-8`}>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {(section.gallery?.items ?? []).map((it, i) => (
              <img key={i} src={it.image_url} alt={it.alt_text || ''} className="h-28 w-full rounded-md object-cover" />
            ))}
            {(section.gallery?.items ?? []).length === 0 && <p className="col-span-full text-sm text-muted-foreground">—</p>}
          </div>
        </section>
      );
    case 'LANDING_SECTION_TYPE_CTA':
      return (
        <section style={styleFor(section)} className={`${wrapClass} px-6 py-10 text-center`}>
          {section.cta?.heading && <h3 className="text-xl font-semibold">{section.cta.heading}</h3>}
          {section.cta?.subheading && <p className="mt-1 text-sm opacity-80">{section.cta.subheading}</p>}
          <div className="mt-4 flex justify-center gap-3">
            {section.cta?.primary_button?.label && (
              <a href={section.cta.primary_button.url} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
                {section.cta.primary_button.label}
              </a>
            )}
            {section.cta?.secondary_button?.label && (
              <a href={section.cta.secondary_button.url} className="rounded-md border border-border px-4 py-2 text-sm font-medium">
                {section.cta.secondary_button.label}
              </a>
            )}
          </div>
        </section>
      );
    case 'LANDING_SECTION_TYPE_FEATURES':
      return (
        <section style={styleFor(section)} className={`${wrapClass} px-6 py-10`}>
          {section.title && <h3 className="mb-4 text-center text-xl font-semibold">{section.title}</h3>}
          <div className={`grid gap-4 ${FEATURE_COLS[Math.min(Math.max(section.features?.columns || 3, 1), 4)]}`}>
            {(section.features?.items ?? []).map((f, i) => (
              <div key={i} className="rounded-md border border-border p-4">
                {f.icon && <span className="text-2xl">{f.icon}</span>}
                <h4 className="mt-2 font-medium">{f.title}</h4>
                {f.description && <p className="mt-1 text-sm text-muted-foreground">{f.description}</p>}
              </div>
            ))}
          </div>
        </section>
      );
    case 'LANDING_SECTION_TYPE_FORM':
      return (
        <section style={styleFor(section)} className={`${wrapClass} px-6 py-8`}>
          {section.form?.heading && <h3 className="mb-1 text-lg font-semibold">{section.form.heading}</h3>}
          {section.form?.description && <p className="mb-3 text-sm text-muted-foreground">{section.form.description}</p>}
          <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            {section.form?.form?.id ? `Form: ${section.form.form.id}` : '—'}
          </div>
        </section>
      );
    case 'LANDING_SECTION_TYPE_BOOKING':
      return (
        <section style={styleFor(section)} className={`${wrapClass} px-6 py-8`}>
          {section.booking?.heading && <h3 className="mb-1 text-lg font-semibold">{section.booking.heading}</h3>}
          {section.booking?.description && <p className="mb-3 text-sm text-muted-foreground">{section.booking.description}</p>}
          <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            {section.booking?.bookable_resource?.id ? `Booking: ${section.booking.bookable_resource.id}` : '—'}
          </div>
        </section>
      );
    case 'LANDING_SECTION_TYPE_VIDEO':
      return (
        <section style={styleFor(section)} className={`${wrapClass} px-6 py-8 text-center`}>
          <div className="mx-auto aspect-video max-w-2xl rounded-md bg-muted" />
          {section.video?.caption && <p className="mt-2 text-xs text-muted-foreground">{section.video.caption}</p>}
        </section>
      );
    case 'LANDING_SECTION_TYPE_FAQ':
      return (
        <section style={styleFor(section)} className={`${wrapClass} px-6 py-8`}>
          {section.title && <h3 className="mb-3 text-lg font-semibold">{section.title}</h3>}
          <div className="space-y-2">
            {(section.faq?.items ?? []).map((f, i) => (
              <details key={i} className="rounded-md border border-border p-3">
                <summary className="cursor-pointer text-sm font-medium">{f.question}</summary>
                <p className="mt-2 text-sm text-muted-foreground">{f.answer}</p>
              </details>
            ))}
          </div>
        </section>
      );
    case 'LANDING_SECTION_TYPE_TESTIMONIALS':
      return (
        <section style={styleFor(section)} className={`${wrapClass} px-6 py-8`}>
          <div className="grid gap-3 sm:grid-cols-2">
            {(section.testimonials?.items ?? []).map((it, i) => (
              <blockquote key={i} className="rounded-md border border-border p-3 text-sm">
                <p className="italic">“{it.quote}”</p>
                <footer className="mt-2 text-xs text-muted-foreground">
                  {it.author_name}
                  {it.author_title ? `, ${it.author_title}` : ''}
                </footer>
              </blockquote>
            ))}
          </div>
        </section>
      );
    case 'LANDING_SECTION_TYPE_CONTACTS':
      return (
        <section style={styleFor(section)} className={`${wrapClass} px-6 py-8 text-sm`}>
          {section.contacts?.heading && <h3 className="mb-2 text-lg font-semibold">{section.contacts.heading}</h3>}
          <ul className="space-y-1 text-muted-foreground">
            {section.contacts?.email && <li>{section.contacts.email}</li>}
            {section.contacts?.phone && <li>{section.contacts.phone}</li>}
            {section.contacts?.address && <li>{section.contacts.address}</li>}
            {section.contacts?.working_hours && <li>{section.contacts.working_hours}</li>}
          </ul>
        </section>
      );
    case 'LANDING_SECTION_TYPE_MAP':
      return (
        <section style={styleFor(section)} className={`${wrapClass} px-6 py-8`}>
          <div className="flex h-48 items-center justify-center rounded-md bg-muted text-xs text-muted-foreground">
            {section.map?.marker_title || `${section.map?.latitude}, ${section.map?.longitude}`}
          </div>
        </section>
      );
    case 'LANDING_SECTION_TYPE_HTML':
      return <section style={styleFor(section)} className={`${wrapClass} px-6 py-8`} dangerouslySetInnerHTML={{ __html: section.html?.html ?? '' }} />;
    case 'LANDING_SECTION_TYPE_DIVIDER':
      return <hr className="my-4 border-border" />;
    default:
      return null;
  }
}
